import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSources } from '../services/api';
import { readJSON, writeJSON } from '../lib/storage';
import { REGION_ORDER } from '../lib/facets';
import { useAuth } from './AuthContext';

const SourcesContext = createContext(null);

const SCOPE_KEY = 'lsearch-scope-v1';
export const CUSTOM_DB_SOURCE = 'Local File Search';
export const customFolderGroup = (folder) => `${folder} - Custom Databases`;

export const DEFAULT_SCOPE = { excludedLabs: [], excludedFolders: [], mode: 'cached', match: 'titles' };

/** Group /sources entries into labs (display groups) with freshness metadata. */
export const deriveLabs = (sources) => {
  const groups = new Map();
  let customFolders = [];
  sources.forEach((s) => {
    if (s.source_name === CUSTOM_DB_SOURCE) {
      customFolders = s.subfolders || [];
      return;
    }
    if (!groups.has(s.display_group)) groups.set(s.display_group, []);
    groups.get(s.display_group).push(s);
  });

  const labs = Array.from(groups, ([name, members]) => {
    const regions = Array.from(new Set(members.map((s) => s.region).filter(Boolean)));
    const withData = members.filter((s) => s.has_local_data);
    return {
      id: name,
      name,
      sources: members,
      sourceNames: members.map((s) => s.source_name),
      region: regions.length === 1 ? regions[0] : 'National',
      regions,
      hasLocalData: withData.length > 0,
      liveOnly: withData.length === 0,
      recordCount: members.reduce((sum, s) => sum + (s.record_count || 0), 0),
      dataTimestamp: Math.max(0, ...members.map((s) => s.data_timestamp || 0)),
      homepage: members.length === 1 ? members[0].homepage : '',
    };
  }).sort((a, b) => a.name.localeCompare(b.name));

  return { labs, customFolders };
};

export const groupLabsByRegion = (labs) => {
  const byRegion = new Map();
  labs.forEach((lab) => {
    if (!byRegion.has(lab.region)) byRegion.set(lab.region, []);
    byRegion.get(lab.region).push(lab);
  });
  return Array.from(byRegion).sort(
    ([a], [b]) => (REGION_ORDER.indexOf(a) + 1 || 99) - (REGION_ORDER.indexOf(b) + 1 || 99),
  );
};

/**
 * Lab metadata from /sources plus the user's saved search scope (which labs
 * to search, cached vs live, titles vs full text).
 */
export const SourcesProvider = ({ children }) => {
  const { canSearchLive } = useAuth();
  const [state, setState] = useState({ status: 'loading', sources: [], error: '' });
  const [scope, setScopeState] = useState(() => ({ ...DEFAULT_SCOPE, ...readJSON(SCOPE_KEY, {}) }));

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    try {
      const sources = await fetchSources();
      setState({ status: 'ready', sources, error: '' });
    } catch (e) {
      setState({ status: 'error', sources: [], error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setScope = useCallback((next) => {
    setScopeState(next);
    writeJSON(SCOPE_KEY, next);
  }, []);

  const value = useMemo(() => {
    const { labs, customFolders } = deriveLabs(state.sources);
    const sourcesByName = Object.fromEntries(state.sources.map((s) => [s.source_name, s]));
    const groupSizes = Object.fromEntries(labs.map((l) => [l.id, l.sources.length]));

    const isAvailable = (lab, mode) => mode === 'live' || !lab.liveOnly || canSearchLive;
    const isIncluded = (lab) => !scope.excludedLabs.includes(lab.id);
    const effectiveMode = (mode) => (mode === 'live' && canSearchLive ? 'live' : 'cached');

    /** What to send for a search in `mode`, and which result groups to expect. */
    const searchTargets = (requestedMode) => {
      const mode = effectiveMode(requestedMode);
      const selectedLabs = labs.filter((lab) => isIncluded(lab) && isAvailable(lab, mode));
      const folders = mode === 'cached'
        ? customFolders.filter((f) => !scope.excludedFolders.includes(f))
        : [];
      const expected = {};
      selectedLabs.forEach((lab) => { expected[lab.id] = lab.sources.length; });
      folders.forEach((f) => { expected[customFolderGroup(f)] = 1; });
      const sources = selectedLabs.flatMap((lab) => lab.sourceNames);
      if (folders.length) sources.push(CUSTOM_DB_SOURCE);
      return { mode, sources, customFolders: folders, expected, labCount: selectedLabs.length + folders.length };
    };

    return {
      ...state,
      reload: load,
      labs,
      customFolders,
      sourcesByName,
      groupSizes,
      scope,
      setScope,
      canSearchLive,
      isAvailable,
      effectiveMode,
      searchTargets,
    };
  }, [state, load, scope, setScope, canSearchLive]);

  return <SourcesContext.Provider value={value}>{children}</SourcesContext.Provider>;
};

export const useSources = () => useContext(SourcesContext);
